import { useEffect, useState } from "react";
import moment from "moment";
import { useHistory } from "react-router";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";

import { interestLevelOptions } from "containers/LoanApplication/constant";

import { LOAN_STATUS } from "constant";

import { createRecord, clearModel, clearActionStatus } from "redux/crud/action";
import { State } from "redux/types";

import { CUSTOMER_LIST_ITEM } from "types/customer";

type PROPS = {
	data: CUSTOMER_LIST_ITEM;
	setLoading: (val: boolean) => void;
};

const CustomerData = ({ data, setLoading }: PROPS) => {
	const user: {
		id: number;
		lending_company: string;
		locations: {
			list: Array<{ group_id: number; name: string; level: number }>;
		};
	} = JSON.parse(localStorage.getItem("tvsd.los.user"));

	const history = useHistory();

	const dispatch = useDispatch();
	const { crudState } = useSelector((st: State) => st);

	useEffect(() => {
		dispatch(clearModel("customerUpdate"));
	}, []);

	const [selectedCustNumber, setSelectedCustNumber] = useState<string>("");

	const handleGenerateLoan = () => {
		setSelectedCustNumber(data?.customer_number);
		dispatch(
			createRecord("newApplication", `/customer-loan`, {
				customerNumber: data?.customer_number,
				identifications: data?.identifications,
			}),
		);
	};

	useEffect(() => {
		if (
			crudState?.newApplication?.actionStatus?.create !== undefined &&
			crudState?.newApplication.actionStatus.create.pending === false &&
			crudState?.newApplication?.actionStatus?.create?.success
		) {
			setLoading(false);
			const newLoanApplicationId =
				crudState?.newApplication?.actionStatus?.create.payload.data.data
					.newLoanApplicationId;
			if (selectedCustNumber) {
				history.push(
					`/loans/new-application/${newLoanApplicationId}/${selectedCustNumber}`,
				);
			}
			dispatch(clearActionStatus("newApplication"));
			dispatch(clearModel("newApplication"));
		} else if (
			crudState?.newApplication !== undefined &&
			crudState?.newApplication?.actionStatus?.create?.pending === true
		) {
			setLoading(true);
		}
	}, [crudState?.newApplication]);

	const displayInterestLevel = () => {
		const selectedInterestLevel = [
			{
				id: 1,
				name: "Hot",
				icon: <HotCircle />,
			},
			{
				id: 2,
				name: "Warm",
				icon: <WarmCircle />,
			},
			{
				id: 3,
				name: "Cold",
				icon: <ColdCircle />,
			},
		].find((item) => item.id === data.interest_level_id);

		if (selectedInterestLevel) {
			return (
				<InterestLevelValue>
					{selectedInterestLevel?.icon}
					<ContentText>{selectedInterestLevel?.name}</ContentText>
				</InterestLevelValue>
			);
		} else {
			return "-";
		}
	};

	const disableNewApplicationButton = () => {
		return data?.existingLoans?.some((d) =>
			[
				LOAN_STATUS.PENDING_SUBMISSION,
				LOAN_STATUS.RESUBMISSION,
				LOAN_STATUS.RECALLED,
				LOAN_STATUS.CONDITIONAL_APPROVAL,
				LOAN_STATUS.UNDER_VERIFICATION,
				LOAN_STATUS.CREDIT_REVIEW,
				LOAN_STATUS.PENDING_INVESTIGATION,
				LOAN_STATUS.PENDING_REVIEW,
				LOAN_STATUS.UNDER_EVALUATION,
				LOAN_STATUS.MANAGER_REVIEW,
			].includes(d.loan_status),
		);
	};

	return (
		<CustomerContainer>
			<CustomerSubTopContainer
				onClick={() => {
					history.push(`/customer-profile/${data.customer_number}`);
				}}>
				<TopGridItem>
					<TitleText>Customer ID</TitleText>
					<CustomerIdText>{data.customer_number}</CustomerIdText>
				</TopGridItem>
				<TopGridItem>
					<TitleText>Customer Source</TitleText>
					<ContentText>{data.source_name || "-"}</ContentText>
				</TopGridItem>
				<TopGridItem>
					<TitleText>First Name</TitleText>
					<ContentText>
						<ElipisisText>{data.first_name || "-"}</ElipisisText>
					</ContentText>
				</TopGridItem>
				<TopGridItem>
					<TitleText>Middle Name</TitleText>
					<ContentText>
						<ElipisisText>{data.middle_name || "-"}</ElipisisText>
					</ContentText>
				</TopGridItem>
				<TopGridItem>
					<TitleText>Last Name</TitleText>
					<ContentText>
						<ElipisisText>{data.last_name || "-"}</ElipisisText>
					</ContentText>
				</TopGridItem>
				<MidGridItem>
					<TitleText>Mobile No.</TitleText>
					<ContentText>{data.mobile_number || "-"}</ContentText>
				</MidGridItem>
				<MidGridItem>
					<TitleText>Date of Birth</TitleText>
					<ContentText>
						{data.date_of_birth
							? moment(data.date_of_birth).format("DD MMMM yy")
							: "-"}
					</ContentText>
				</MidGridItem>
				<MidGridItem>
					<TitleText>Interest</TitleText>
					{displayInterestLevel()}
				</MidGridItem>
				<MidGridItem>
					<TitleText>Updated Date</TitleText>
					<ContentText>
						{data.updatedAt
							? moment(data.updatedAt).format("DD MMMM yy, HH:MM:SS a")
							: "-"}
					</ContentText>
				</MidGridItem>
				<MidGridItem>
					<TitleText>Created Date</TitleText>
					<ContentText>
						{data.createdAt
							? moment(data.createdAt).format("DD MMMM yy, HH:MM:SS a")
							: "-"}
					</ContentText>
				</MidGridItem>

				<BottomGridItem>
					<TitleText>Branch</TitleText>
					<ContentText>{data.branch_name || "-"}</ContentText>
				</BottomGridItem>
				<BottomGridItem>
					<TitleText>Sales Agent</TitleText>
					<ContentText>{data.sales_agent_name || "-"}</ContentText>
				</BottomGridItem>
				<BottomGridItem>
					<TitleText>Investigator</TitleText>
					<ContentText>{data.investigator || "-"}</ContentText>
				</BottomGridItem>
				<BottomGridItem>
					<TitleText>Applications</TitleText>
					<ContentText>{data.applications_number}</ContentText>
				</BottomGridItem>
			</CustomerSubTopContainer>
			<CustomerSubBottomContainer>
				<ExistingLoanChipContainer>
					{data?.existingLoans?.map((d, idx) => {
						const isButtonDisabled = !user.locations.list.some(
							(v) => v.group_id === d.group_id,
						);

						const handleRoute = () => {
							if (d.loan_status.toLowerCase() === "pending submission") {
								history.push(
									`/loans/new-application/${d.loan_application_id}/${d.customer_number}`,
								);
							} else {
								history.push(
									`/loan-details/${d.loan_application_id}/${d.customer_number}`,
								);
							}
						};

						return (
							<ExistingLoanChip
								key={idx.toString()}
								{...(!isButtonDisabled && {
									style: {
										cursor: "pointer",
									},
									onClick: handleRoute,
								})}>
								<img
									src={`${process.env.PUBLIC_URL}/images/icon-doc-loan.svg`}
									alt="doc-loan-icon"
									width={24}
									height={24}
								/>
								<LoanIdText>{`${d.loan_application_id}:`}</LoanIdText>
								<LoanStatusText>{d.loan_status}</LoanStatusText>
							</ExistingLoanChip>
						);
					})}
				</ExistingLoanChipContainer>
				<NewApplicationButton
					style={{
						cursor: disableNewApplicationButton() ? "default" : "pointer",
						backgroundColor: disableNewApplicationButton() ? "#9ea5e5" : "#3548C9",
					}}
					onClick={() => {
						if (!disableNewApplicationButton()) {
							handleGenerateLoan();
						}
					}}>
					<img
						src={`${process.env.PUBLIC_URL}/images/add-button.svg`}
						alt="add-icon"
						width={24}
						height={24}
					/>
					<NewApplicationText>New Application</NewApplicationText>
				</NewApplicationButton>
			</CustomerSubBottomContainer>
		</CustomerContainer>
	);
};

const CustomerContainer = styled.div`
	min-height: 361px;
	width: 100%;
	border-radius: 16px;
	background: #ffffff;
	margin-top: 8px;
	margin-bottom: 20px;
	border: 1px solid #e7e9ed;
`;

const CustomerSubTopContainer = styled.div`
	padding: 24px;
	height: 297px;
	display: grid;
	grid-template-columns: 5fr 5fr 5fr 6fr 6fr;
	grid-template-rows: 1fr 1fr 1fr;
	cursor: pointer;
`;

const TopGridItem = styled.div`
	padding-top: 12px;
`;

const MidGridItem = styled.div`
	padding-top: 24px;
	padding-bottom: 24px;
	border-top: 1px solid #e7e9ed;
	border-bottom: 1px solid #e7e9ed;
`;

const BottomGridItem = styled.div`
	padding-top: 24px;
`;

const TitleText = styled.div`
	font-family: Inter;
	line-height: 16px;
	font-size: 12px;
	color: #253858;
`;

const CustomerIdText = styled.div`
	margin-top: 5px;
	font-family: Inter;
	line-height: 19px;
	font-weight: 700;
	font-size: 14px;
	color: #3548c9;
`;

const ContentText = styled.div`
	margin-top: 5px;
	font-family: Inter;
	line-height: 19px;
	font-size: 14px;
	color: #253858;
`;

const ElipisisText = styled.div`
	width: 175px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const InterestLevelValue = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	gap: 4px;
`;

const CustomerSubBottomContainer = styled.div`
	padding: 12px 24px;
	width: 100%;
	min-height: 64px;
	display: grid;
	grid-template-columns: 1fr auto;
	grid-template-rows: 1fr;
	border-bottom-left-radius: 16px;
	border-bottom-right-radius: 16px;
	background-color: #c5c8ef;
`;

const ExistingLoanChipContainer = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
`;

const ExistingLoanChip = styled.div`
	margin-right: 16px;
	margin-bottom: 10px;
	height: 30px;
	padding: 2px 12px;
	display: flex;
	align-items: center;
	background-color: #e7e9ed;
	border-radius: 100px;
`;

const LoanIdText = styled.div`
	margin-left: 4px;
	margin-right: 4px;
	font-family: Inter;
	line-height: 19px;
	font-size: 14px;
	color: #395278;
`;

const LoanStatusText = styled.div`
	font-family: Inter;
	line-height: 19px;
	font-size: 14px;
	font-weight: 700;
	color: #395278;
`;

const NewApplicationButton = styled.div`
	width: 155px;
	height: 40px;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
`;

const NewApplicationText = styled.div`
	font-family: Inter;
	font-size: 14px;
	line-height: 19px;
	color: #ffffff;
`;

const HotCircle = styled.div`
	width: 16px;
	height: 16px;
	border-radius: 8px;
	background-color: #eb4553;
`;

const WarmCircle = styled.div`
	width: 16px;
	height: 16px;
	border-radius: 8px;
	background-color: #ff9600;
`;

const ColdCircle = styled.div`
	width: 16px;
	height: 16px;
	border-radius: 8px;
	background-color: #3ba0d7;
`;

export default CustomerData;
